import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ChartLine, MagnifyingGlass, FileText, SignOut, Sparkle, EnvelopeSimple, Star, UserCircle, ChatCircleDots, Buildings, Users, CreditCard, MegaphoneSimple, List, SidebarSimple } from '@phosphor-icons/react';
import VCChat from './VCChat';
import IncubatorApp from '../incubator/IncubatorApp';
import VCPortfolio from './VCPortfolio';
import VCDiscover from './VCDiscover';
import VCReports from './VCReports';
import VCInvite from './VCInvite';
import VCTracker from './VCTracker';
import VCProfile from './VCProfile';
import VCPrograms from './VCPrograms';
import IncubatorCompanyRecordPage from '../incubator/IncubatorCompanyRecordPage';
import Studio from '../Studio';
import Team from '../Team';
import Billing from '../Billing';
import LiveTicker from '../../components/LiveTicker';
import AIBubble from '../../components/AIBubble';
import TrialBanner from '../../components/TrialBanner';
import OnboardingTour, { SIDEBAR_PIN_EVENT } from '../../components/OnboardingTour';
import { SidebarProfileMenu } from '../../components/SidebarProfileMenu';
import { ModuleGate, useModuleAccess } from '@/moduleAccess';
import api from '@/api';

// Section heading with a rule under it. Collapsed, the rail is too narrow for
// text — the rule alone keeps the grouping readable. (Mirrors StartupAppNew's.)
const SidebarSectionLabel = ({ children, expanded }) => (
  expanded ? (
    <div className="px-1 pt-1 pb-1.5 mb-1 border-b border-border text-[10px] uppercase tracking-wider text-text-muted font-semibold truncate">
      {children}
    </div>
  ) : (
    <div className="mx-1 mt-1 mb-2 border-b border-border" title={children} />
  )
);

const VCApp = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const companyBackground=location.pathname.startsWith('/vc/company/')?location.state?.companyBackground:null;
  const { isEnabled } = useModuleAccess();
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [sidebarPinned, setSidebarPinned] = useState(false); // held open by the product tour
  // Open by default on every load — collapsing is a this-session-only choice
  // via the "<" toggle, not a remembered preference, independent of the
  // tour's temporary pin above.
  const [sidebarDocked, setSidebarDocked] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const sidebarExpanded = mobileSidebarOpen || sidebarHovered || sidebarPinned || sidebarDocked;

  const toggleSidebarDock = () => setSidebarDocked((prev) => !prev);
  const [tickerEvents, setTickerEvents] = useState([]);
  const [portfolio, setPortfolio] = useState({ aggregates: {}, companies: [] });
  const [workspaceLogo, setWorkspaceLogo] = useState(user?.logo_url || '');
  const [workspaceName, setWorkspaceName] = useState(user?.fund_name || user?.company_name || '');
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [portfolioError, setPortfolioError] = useState('');
  const [entityType, setEntityType] = useState(user?.entity_type || 'vc');
  // T+14 on a lapsed contract (vc_billing.py): the nav stays usable, every
  // page except Billing is greyed out and can't be clicked.
  const [billingPaused, setBillingPaused] = useState(false);

  const isIncubator = entityType === 'incubator';
  const productLocked = billingPaused && !location.pathname.startsWith('/vc/billing');

  // The product tour pins the rail open so its steps land on labelled rows.
  useEffect(() => {
    const onPin = (e) => setSidebarPinned(!!e.detail?.pinned);
    window.addEventListener(SIDEBAR_PIN_EVENT, onPin);
    return () => window.removeEventListener(SIDEBAR_PIN_EVENT, onPin);
  }, []);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname]);

  // Re-checked on every navigation so paying on Billing un-greys the app
  // as soon as the user moves on, without a reload.
  useEffect(() => {
    api.get('/billing/status').then((r) => setBillingPaused(!!r.data?.contract?.paused)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    fetchTickerEvents();
    fetchPortfolio();
    fetchEntityType();
    const interval = setInterval(fetchTickerEvents, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchEntityType = async () => {
    try {
      const res = await api.get('/vc/fund');
      const et = res.data?.entity_type || user?.entity_type || 'vc';
      setWorkspaceName(res.data?.fund_name || '');
      setWorkspaceLogo(res.data?.logo_url || '');
      setEntityType(et);
      // Persist on user so future loads pick it up
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      u.entity_type = et;
      localStorage.setItem('user', JSON.stringify(u));
    } catch (err) { /* swallow */ }
  };

  const fetchTickerEvents = async () => {
    try {
      const response = await api.get('/vc/ticker');
      setTickerEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch ticker', error);
    }
  };

  const refreshTicker = async () => {
    try {
      await api.post('/vc/ticker/refresh', {});
      await fetchTickerEvents();
    } catch (error) {
      console.error('Ticker refresh failed', error);
    }
  };

  const fetchPortfolio = async () => {
    setPortfolioLoading(true);
    setPortfolioError('');
    try {
      const response = await api.get('/vc/portfolio');
      setPortfolio(response.data);
    } catch (error) {
      console.error('Failed to fetch portfolio', error);
      setPortfolioError('We could not load your portfolio. Please try again.');
    } finally { setPortfolioLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  // Sparkle is the product's AI glyph (same as the startup AI Coach) — the old
  // pie-chart icon read as analytics, not a co-pilot. It's pulled out as a
  // standalone CTA above the grouped nav, not just another row in the list.
  const aiCoPilotItem = { path: '/vc/chat', icon: Sparkle, label: 'AI Co-Pilot', testId: 'nav-chat' };

  const navGroups = [
    {
      key: 'portfolio',
      label: 'Portfolio',
      items: [
        { path: '/vc/portfolio', icon: ChartLine, label: isIncubator ? 'Companies' : 'Portfolio', testId: 'nav-portfolio' },
        { path: '/vc/tracker', icon: Star, label: 'Tracker', testId: 'nav-tracker' },
        { path: '/vc/reports', icon: FileText, label: 'Reports', testId: 'nav-reports' },
      ],
    },
    {
      key: 'pipeline',
      label: 'Pipeline',
      items: [
        { path: '/vc/programs', icon: Buildings, label: isIncubator ? 'Programs' : 'Watchlists', testId: 'nav-programs' },
        { path: '/vc/discover', icon: MagnifyingGlass, label: 'Discover', testId: 'nav-discover' },
        { path: '/vc/invite', icon: EnvelopeSimple, label: 'Invite', testId: 'nav-invite' },
      ],
    },
    {
      key: 'studios',
      label: 'Studios',
      items: [
        { path: '/vc/studio', icon: MegaphoneSimple, label: 'Studios', testId: 'nav-studio', module: 'vc.studio' },
      ],
    },
  ];

  if (isIncubator) return <IncubatorApp user={{ ...user, fund_name: workspaceName || user?.fund_name, logo_url: workspaceLogo || user?.logo_url }} portfolio={portfolio} loading={portfolioLoading} error={portfolioError} onRefresh={fetchPortfolio} onLogout={handleLogout} pinned={sidebarPinned} />;

  return (
    <div className="h-screen bg-background flex overflow-hidden">
      {mobileSidebarOpen && (
        <button type="button" aria-label="Close navigation" onClick={() => setMobileSidebarOpen(false)} className="fixed inset-0 z-40 bg-black/40 md:hidden" />
      )}
      {/* Full-height rail — the header/ticker live in the content column to its
          right, not above it, so this pane runs edge-to-edge top to bottom. */}
      <aside
        className={`bg-surface border-r border-border transition-all duration-200 flex flex-col shrink-0 fixed md:relative inset-y-0 left-0 z-50 md:z-auto h-full w-[260px] ${
          sidebarExpanded ? 'md:w-[230px]' : 'md:w-[48px]'
        } ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        {/* The Founderboard brand lives here ONLY — the top bar next to it never
            repeats it. Collapsed rail: logo only. Expanded: logo + name + dock toggle. */}
        <div className={`hidden md:flex items-center px-2 pt-2.5 pb-1.5 ${sidebarExpanded ? 'justify-between' : 'justify-center'}`}>
          <div className="flex items-center gap-1.5 min-w-0">
            <img src="/founderboard-logo.png" alt="Founderboard" className="w-6 h-6 object-contain flex-shrink-0 rounded" />
            {sidebarExpanded && <span className="text-[13px] font-heading font-semibold text-text-primary truncate">Founderboard</span>}
          </div>
          {sidebarExpanded && (
              <button
                type="button"
                onClick={toggleSidebarDock}
                className={`flex items-center justify-center w-7 h-7 rounded-md border transition-colors flex-shrink-0 ${
                  sidebarDocked ? 'border-primary/40 text-primary bg-primary/10' : 'border-border text-text-muted hover:text-text-primary hover:border-primary/40'
                }`}
                data-testid="sidebar-dock-toggle"
                title={sidebarDocked ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                <SidebarSimple size={15} weight="bold" />
              </button>
            )}
          </div>
          {isIncubator && (
            <div className="px-2 pt-2" data-testid="incubator-mode-badge">
              {sidebarExpanded ? (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-warning/10 border border-warning/30 rounded-md">
                  <div className="w-1.5 h-1.5 rounded-full bg-warning animate-pulse" />
                  <span className="text-[10px] font-mono font-semibold tracking-wider text-warning uppercase">Incubator</span>
                </div>
              ) : (
                <div className="w-full flex items-center justify-center py-1.5" title="Incubator Mode">
                  <div className="w-2 h-2 rounded-full bg-warning animate-pulse" />
                </div>
              )}
            </div>
          )}

          {/* Standalone highlighted CTA — not just another row in the list. */}
          <div className="px-2 pt-2">
            <button
              onClick={() => navigate(aiCoPilotItem.path)}
              title={sidebarExpanded ? undefined : aiCoPilotItem.label}
              className="w-full flex items-center gap-3 p-2 rounded-lg bg-primary/10 text-primary font-semibold hover:bg-primary/15 transition-colors"
              data-testid={aiCoPilotItem.testId}
            >
              <Sparkle size={20} weight="fill" className="shrink-0" />
              {sidebarExpanded && <span className="text-sm flex-1 text-left truncate">{aiCoPilotItem.label}</span>}
            </button>
          </div>

          <div className="p-2 flex-1 overflow-y-auto overflow-x-hidden space-y-4">
            {navGroups.map((group) => (
              <nav key={group.key} className="space-y-1.5" data-testid={`nav-group-${group.key}`}>
                <SidebarSectionLabel expanded={sidebarExpanded}>{group.label}</SidebarSectionLabel>
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  const locked = item.module && !isEnabled(item.module);
                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      title={sidebarExpanded ? undefined : item.label}
                      className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all ${
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-text-muted hover:text-text-primary hover:bg-background'
                      }`}
                      data-testid={item.testId}
                    >
                      <Icon size={20} weight="bold" className="shrink-0" />
                      {sidebarExpanded && <span className="text-sm font-medium flex-1 text-left truncate">{item.label}</span>}
                      {sidebarExpanded && locked && (
                        <span className="text-[9px] uppercase tracking-wider font-semibold text-text-muted" data-testid={`${item.testId}-soon`}>soon</span>
                      )}
                    </button>
                  );
                })}
              </nav>
            ))}
          </div>

          <div className="p-2 border-t border-border" data-testid="nav-group-general">
            <SidebarProfileMenu
              name={user?.full_name || user?.email}
              expanded={sidebarExpanded}
              onLogout={handleLogout}
              basePath="/vc"
            />
          </div>
        </aside>

        <div className="flex-1 min-w-0 flex flex-col">
          {isIncubator && <div className="h-[2px] w-full bg-warning shrink-0" data-testid="incubator-accent-strip" />}
          <TrialBanner />
          <div className={productLocked ? 'grayscale opacity-50 pointer-events-none select-none' : ''} inert={productLocked}>
            <LiveTicker events={tickerEvents} onRefresh={refreshTicker} />
          </div>
          <header className="md:hidden h-[52px] bg-surface border-b border-border flex items-center justify-between px-4 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <button type="button" onClick={() => setMobileSidebarOpen(true)} className="w-9 h-9 -ml-2 rounded-lg flex items-center justify-center text-text-secondary hover:bg-background" aria-label="Open navigation">
                <List size={21} weight="bold" />
              </button>
              <img src="/founderboard-logo.png" alt="Founderboard" className="w-6 h-6 object-contain" />
              <span className="text-[13px] font-heading font-semibold text-text-primary truncate">Founderboard</span>
            </div>
          </header>

          <main className={`flex-1 overflow-y-auto relative ${productLocked ? 'grayscale opacity-50 pointer-events-none select-none' : ''}`}
            inert={productLocked} aria-disabled={productLocked || undefined} data-testid="vc-main" data-locked={productLocked ? 'true' : undefined}>
          <div style={{display:'contents'}} inert={companyBackground?true:undefined} aria-hidden={companyBackground?true:undefined}><Routes location={companyBackground||location}>
            <Route path="/chat" element={<VCChat user={user} />} />
            <Route path="/portfolio" element={<VCPortfolio user={user} portfolio={portfolio} onRefresh={fetchPortfolio} />} />
            <Route path="/company/:recordId" element={<IncubatorCompanyRecordPage investor />} />
            <Route path="/programs" element={<VCPrograms user={user} entityType={entityType} />} />
            <Route path="/tracker" element={<VCTracker user={user} />} />
            <Route path="/discover" element={<VCDiscover user={user} />} />
            <Route path="/invite" element={<VCInvite user={user} />} />
            <Route path="/reports" element={<VCReports user={user} />} />
            <Route path="/studio" element={<ModuleGate module="vc.studio" accent="var(--primary)"><Studio user={user} /></ModuleGate>} />
            <Route path="/team" element={<Team user={user} />} />
            <Route path="/billing" element={<Billing user={user} />} />
            <Route path="/profile" element={<VCProfile user={user} />} />
            <Route path="/" element={<VCChat user={user} />} />
          </Routes></div>
          {companyBackground&&<Routes><Route path="/company/:recordId" element={<IncubatorCompanyRecordPage investor />} /></Routes>}
          </main>
        </div>
      {!billingPaused && <AIBubble user={user} />}
      <OnboardingTour role={isIncubator ? 'incubator' : 'vc'} />
    </div>
  );
};

export default VCApp;
